import { doEat2 } from "./feed";
import { doBirth, doSex2 } from "./procreation";
import {
  CharacterState,
  CharacterHandler as CH,
  isAlive,
  isDead,
} from "./characters";
import { ResourceHandler, ResourceNodeState } from "./resources";
import { doMove } from "./movements";
import { HuntHandler } from "./hunt";
import { AnimalHandler } from "./animals";
import { Faction, FactionType, SocialHandler } from "./social";
import { Dictionary } from "highcharts";
import { War, WarHandler } from "./war";
//@TODO: FETCH/SAVE/LOAD CONSTANT VALUES FROM google spreadsheet

export enum Experiments {
  LASTNAME_TO_GENES = "LASTNAME_TO_GENES",
  FEED_DEBUG = "FEED_DEBUG",
}

export enum ActionType {
  EAT = "EAT",
  HUNT = "HUNT",
  MOVE = "MOVE",
  AGE = "AGE",
  DIE = "DIE",
  SEX = "SEX",
  UPDATE_STATUS_LIST = "UPDATE_STATUS_LIST",
  BIRTH = "BIRTH",
  WAR = "WAR",
  FOOD_INCREASE = "FOOD_INCREASE",
  PREY_REPRODUCE = "PREY_REPRODUCE",
}

//@TODO: system to sanitize action order requirements
export const orderedActionsAll = [
  ActionType.AGE,
  ActionType.HUNT,
  ActionType.EAT,
  ActionType.DIE,
  ActionType.SEX,
  ActionType.BIRTH,
  ActionType.MOVE,
  ActionType.WAR,
  ActionType.UPDATE_STATUS_LIST,
  ActionType.PREY_REPRODUCE,
];

export enum AgeType {
  CYCLE,
  YEAR,
}

export type IAgenda = {
  actions: Action[];
  plan: () => IAgenda; //mise à jour de l'agenda
  getNext: () => Action;
};

export class Agenda {
  actions: Action[];

  constructor() {
    this.actions = [];
  }
}

export type WorldPos = {
  x: number;
  y: number;
};

export type ActionState = {
  type: ActionType;
  isActive: boolean;
};

export type WorldState = {
  cycles: number;
  size: number;
  characters: CharacterState[];
  deads: CharacterState[];
  resources: ResourceNodeState[];
  factions: Faction<FactionType>[];
  actionsPerTurn: ActionState[];
  wars: War<FactionType>[];
  experiments: Experiments[];
};

export type Action = {
  type: ActionType;
  payload: any;
};

export interface IPosable {
  pos: WorldPos;
}

interface IDable {
  id: string;
}

export const sortByPos = <T extends IPosable>(p1: T, p2: T): number => {
  return (p1.pos.y - p2.pos.y) * 10 + (p1.pos.x - p2.pos.x);
};

export type SplitByPosMeta = {
  pos: string;
  from: number;
  to: number;
  quantity: number;
};

export const groupByPos =
  <T extends IPosable>() =>
  (groups: { [key: string]: T[] }, e: T): { [key: string]: T[] } => {
    return groups[`${e.pos.x}x${e.pos.y}`]
      ? {
          ...groups,
          ...{
            [`${e.pos.x}x${e.pos.y}`]: [...groups[`${e.pos.x}x${e.pos.y}`], e],
          },
        }
      : { ...groups, ...{ [`${e.pos.x}x${e.pos.y}`]: [e] } };
  };

export const excludeFrom = <T extends IDable>(
  origin: T[],
  excluded: T[]
): T[] => origin.filter((oe) => excluded.every((ee) => ee.id !== oe.id));

export const applyOnMatch = <T>(
  origin: T[],
  predicate: (e: T) => boolean,
  transform: (e: T) => T
): T[] => origin.map((e) => (predicate(e) ? transform(e) : e));

const doAction = (
  action: Action,
  w: WorldState,
  opts?: { debug: boolean }
): WorldState => {
  if (opts?.debug) {
    console.time(`DO ACTION ${action.type}`);
  }

  const newWorld = (() => {
    switch (action.type) {
      case ActionType.DIE:
        const newCharacters = w.characters.map((c) => CH.doDying(c, w));
        return {
          ...w,
          characters: newCharacters.filter(isAlive),
          deads: [...w.deads, ...newCharacters.filter(isDead)],
        };

      case ActionType.AGE:
        return {
          ...w,
          cycles: w.cycles + 1,
          characters: w.characters.map((c) => CH.doAging(c, w)),
        };

      case ActionType.SEX:
        return doSex2(w);

      case ActionType.BIRTH:
        return doBirth(w);

      case ActionType.WAR:
        return WarHandler.doWar(w);

      case ActionType.MOVE:
        return doMove(w);

      case ActionType.EAT:
        return doEat2(w);

      case ActionType.HUNT:
        return HuntHandler.doHunt2(w);

      case ActionType.UPDATE_STATUS_LIST:
        return {
          ...w,
          characters: w.characters.map((c) => ({
            ...c,
            statusList: c.statusList
              .filter((s) => !s.payload?.weak)
              .map((s) => ({
                ...s,
                duration: s.duration + 1,
              })),
          })),
        };

      case ActionType.FOOD_INCREASE:
        return ResourceHandler.doIncreaseFood(w);

      case ActionType.PREY_REPRODUCE:
        return AnimalHandler.doReproduce(w);

      default:
        throw new Error(`UNKNOWN ActionType: ${action.type}`);
    }
  })();

  if (opts?.debug) {
    console.timeEnd(`DO ACTION ${action.type}`);
  }
  return newWorld;
};

export const createActionStates = (
  actionOrdered: ActionType[]
): ActionState[] => {
  return actionOrdered.map((a) => ({
    type: a,
    isActive: true,
  }));
};

export type WorldOptions = {
  actionsListOrdered?: ActionType[];
  experiments?: Experiments[];
  factions?: Faction<FactionType>[];
};

export class WorldHandler {
  static create = (size: number, opts?: WorldOptions): WorldState => {
    const factionsPos: Faction<FactionType>[] = Array.from(
      Array(size * size)
    ).map((e, i) => {
      const x = i % size;
      const y = Math.floor(i / size);

      return SocialHandler.createFaction(FactionType.POS, `${x}x${y}`);
    });

    return {
      cycles: 0,
      size,
      characters: [],
      deads: [],
      resources: [],
      factions: opts?.factions
        ? [...factionsPos, ...opts?.factions]
        : factionsPos,
      actionsPerTurn: createActionStates(
        opts && opts.actionsListOrdered
          ? opts.actionsListOrdered
          : orderedActionsAll
      ),
      wars: [],
      experiments: opts?.experiments || [],
    };
  };

  static turn = (w: WorldState, opts?: { debug?: boolean }): WorldState => {
    //@TODO: ADD PLAY ONE TURN ONLY

    //@TODO: make it conditionnaly when having a faction POS is guaranted
    const baseWorld = WorldHandler.updateFactionPos(w);

    const newWorld: WorldState = w.actionsPerTurn.reduce(
      (worldAcc: WorldState, action: ActionState) =>
        action.isActive
          ? doAction({ type: action.type, payload: null }, worldAcc, {
              debug: opts?.debug || false,
            })
          : worldAcc,
      { ...baseWorld }
    );

    return newWorld;
  };

  static getPosDictionary = (
    world: WorldState,
    keyProperty: "name" | "id" = "name"
  ): Dictionary<Faction<FactionType>> => {
    return world.factions
      .filter((f) => f.type === FactionType.POS)
      .reduce(
        (dict: Dictionary<Faction<FactionType>>, f: Faction<FactionType>) => ({
          ...dict,
          [f[keyProperty]]: f,
        }),
        {}
      );
  };

  static updateFactionPos = (world: WorldState): WorldState => {
    const posFactionsDict: Dictionary<Faction<FactionType>> =
      WorldHandler.getPosDictionary(world);

    const characters = world.characters.map((c) => {
      const faction = posFactionsDict[WorldHandler.posStr(c.pos)];
      //console.log("factino pos membership:", faction, world.factions);
      return SocialHandler.addFactionMembership(c, faction);
    });

    return {
      ...world,
      characters,
    };
  };

  static getAllCharactersAtPos = (
    pos: WorldPos,
    w: WorldState
  ): CharacterState[] => {
    return w.characters.filter(
      (char) => char.pos.x === pos.x && char.pos.y === pos.y
    );
  };

  static getAllResourceNodesAtPos = (
    pos: WorldPos,
    w: WorldState
  ): ResourceNodeState[] => {
    return w.resources.filter((rn) => rn.pos.x === pos.x && rn.pos.y === pos.y);
  };

  static getCharacter = (
    world: WorldState,
    charId: string
  ): CharacterState | null => {
    const alive = world.characters.find((c) => c.id === charId);

    if (alive) {
      return alive;
    }

    if (!world.deads) {
      console.log(world);
      return null;
    }

    console.log(world);
    const dead = world.deads.find((c) => c.id === charId);
    if (dead) {
      return dead;
    }

    return null;
  };

  static posStr = (p: WorldPos): string => {
    return `${p.x}x${p.y}`;
  };
}
